import { useCallback, useState } from "react";
import {
  createArchitectSession,
  pingArchitectSession,
  streamArchitectMessage,
} from "../api/services/architectAgentService";
import {
  getStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import type { AgentContextUsage } from "../types/agentChat";

const SESSION_KEY = "architect-api-description" as const;

export function useArchitectAgentEnhance(tenantId: string | null) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);

  const ensureSession = useCallback(async (): Promise<string> => {
    const stored = getStoredAgentSession(SESSION_KEY, tenantId);
    if (stored?.sessionId) {
      const ok = await pingArchitectSession(stored.sessionId);
      if (ok) {
        setSessionId(stored.sessionId);
        return stored.sessionId;
      }
    }
    const id = await createArchitectSession();
    setStoredAgentSession(SESSION_KEY, tenantId, id);
    setSessionId(id);
    return id;
  }, [tenantId]);

  const sendMessage = useCallback(
    async (message: string): Promise<string> => {
      setLoading(true);
      setError(null);
      try {
        const id = await ensureSession();
        const result = await streamArchitectMessage(id, message);
        setContextUsage(result.context_usage ?? null);
        return result.response;
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Architect agent failed";
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [ensureSession],
  );

  return { sessionId, loading, error, contextUsage, sendMessage };
}
